'use client';
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import classnames from 'classnames';
import { nanoid } from 'nanoid';
import { Label, FieldNote } from '../../index.js';
export const Slider = (_a) => {
    var { className, id, required, fieldNote, inverted, hideLabel, ariaDescribedBy, isError, optionalLabel, requiredLabel, disabled, isSingle } = _a, other = __rest(_a, ["className", "id", "required", "fieldNote", "inverted", "hideLabel", "ariaDescribedBy", "isError", "optionalLabel", "requiredLabel", "disabled", "isSingle"]);
    const [idVar, setId] = React.useState();
    const [ariaDescribedByVar, setAriaDescribedBy] = React.useState();
    const [input1ValueVar, setInput1Value] = React.useState(30);
    const [input2ValueVar, setInput2Value] = React.useState(70);
    const [input1IsActiveVar, setInput1IsActive] = React.useState(true);
    const [input2IsActiveVar, setInput2IsActive] = React.useState(true);
    const [sliderMaxValue, setSliderMaxValue] = React.useState(0);
    const [percent1Var, setPercent1] = React.useState(30);
    const [percent2Var, setPercent2] = React.useState(70);
    const sliderTrackRef = React.useRef(null);
    const input1Ref = React.useRef(null);
    const input2Ref = React.useRef(null);
    const rangeColor = React.useCallback(() => {
        setPercent1((input1ValueVar / sliderMaxValue) * 100);
        setPercent2((input2ValueVar / sliderMaxValue) * 100);
        if (!sliderTrackRef.current)
            return;
        sliderTrackRef.current.style.background = `
      linear-gradient(
        to right, #dadae5 ${percent1Var}%,
        var(--sparky-theme-color-primary-background) ${percent1Var}%,
        var(--sparky-theme-color-primary-background) ${percent2Var}%,
        #dadae5 ${percent2Var}%
      )
    `;
    }, [
        input1ValueVar,
        input2ValueVar,
        sliderMaxValue,
        percent1Var,
        percent2Var,
    ]);
    const singleColor = React.useCallback(() => {
        setPercent1((input1ValueVar / sliderMaxValue) * 100);
        if (!sliderTrackRef.current)
            return;
        sliderTrackRef.current.style.background = `
      linear-gradient(to right,
      var(--sparky-theme-color-primary-background) ${percent1Var}%,
      var(--sparky-theme-color-primary-background) 0%,
      #dadae5 ${percent1Var}%, #dadae5 100%)
    `;
    }, [input1ValueVar, percent1Var, sliderMaxValue]);
    const slideOne = React.useCallback(() => {
        setInput1IsActive(true);
        setInput2IsActive(false);
        if (!input1Ref.current)
            return;
        setInput1Value(parseInt(input1Ref.current.value));
        if (!isSingle && input1ValueVar >= input2ValueVar) {
            setInput1Value(input2ValueVar);
            setInput1IsActive(false);
            setInput2IsActive(true);
        }
        if (isSingle) {
            singleColor();
        }
        else if (disabled) {
            return;
        }
        else
            rangeColor();
    }, [
        input1ValueVar,
        input2ValueVar,
        isSingle,
        rangeColor,
        singleColor,
        disabled,
    ]);
    const slideTwo = React.useCallback(() => {
        setInput1IsActive(false);
        setInput2IsActive(true);
        if (input2ValueVar <= input1ValueVar) {
            setInput2Value(input1ValueVar);
            setInput1IsActive(true);
            setInput2IsActive(false);
        }
        if (!input2Ref.current)
            return;
        setInput2Value(parseInt(input2Ref.current.value));
        rangeColor();
    }, [
        input1ValueVar,
        input2ValueVar,
        setInput1IsActive,
        setInput2IsActive,
        rangeColor,
    ]);
    React.useEffect(() => {
        if (!input1Ref.current)
            return;
        setSliderMaxValue(parseInt(input1Ref.current.max));
        setId(id || nanoid());
        if (fieldNote) {
            setAriaDescribedBy(ariaDescribedBy || nanoid());
        }
        slideOne();
        slideTwo();
    }, [id, fieldNote, ariaDescribedBy, slideOne, slideTwo]);
    const componentClassName = classnames('sparky-c-slider', className, {
        'sparky-is-disabled': disabled,
        'sparky-c-slider--single': isSingle,
    });
    return (_jsxs("div", Object.assign({ className: componentClassName }, other, { children: [!isSingle && (_jsx(Label, { className: "sparky-c-slider__label", htmlFor: idVar, text: `$${input1ValueVar} - $${input2ValueVar}`, inverted: inverted, bold: false, hideLabel: hideLabel, required: required, optionalLabel: optionalLabel, requiredLabel: requiredLabel })), _jsxs("div", Object.assign({ className: "sparky-c-slider__body" }, { children: [_jsx("div", { className: "sparky-c-slider__track", ref: sliderTrackRef }), _jsx("input", { ref: input1Ref, type: "range", min: "1", max: "100", value: input1ValueVar, onInput: slideOne, onChange: slideOne, disabled: disabled, id: idVar, "aria-describedby": fieldNote ? ariaDescribedByVar : undefined, className: `sparky-c-slider__input ${input1IsActiveVar && 'sparky-is-active'}` }), !isSingle && (_jsx("input", { ref: input2Ref, type: "range", min: "1", max: "100", value: input2ValueVar, onInput: slideTwo, onChange: slideTwo, disabled: disabled, className: `sparky-c-slider__input ${input2IsActiveVar && 'sparky-is-active'}` }))] })), fieldNote && (_jsx(FieldNote, Object.assign({ className: "sparky-c-slider__note", id: ariaDescribedByVar, isError: isError, inverted: inverted }, { children: fieldNote })))] })));
};
//# sourceMappingURL=Slider.js.map